import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '@/contexts/UserContext';
import type { ChartData, ChartOptions } from 'chart.js';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Doughnut, Bar } from 'react-chartjs-2';
import { useProject } from '@/components/ProjectSelector';
import { fetchBillsDashboard, type DashboardData } from '../services/billsDashboardService';

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

// Chart.js/canvas fillStyle cannot resolve CSS custom properties (var(--x)
// strings fail to parse as canvas colors and silently no-op), so these stay
// literal hex even though most exactly match design-system palette entries
// (blue-500, emerald-500, amber-500, red-500, cyan-500, orange-500 --
// #8B5CF6/#84CC16 have no palette match at all). Gap: would need a JS-side
// (non-CSS-var) constants source to centralize -- out of scope here.
const pieColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

export function BillsDashboard() {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const responsiveNumberStyle = { fontSize: 'clamp(1.5rem, 3vw, 1.75rem)' };

  const formatCurrency = (value: number | string) => {
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return '$0.00';
    }
    return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ONE request per (company, project): the backend computes everything. A newer selection cancels the older request,
  // so a slow answer for the previous project can never overwrite the current one.
  useEffect(() => {
    if (!companyId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchBillsDashboard(selectedProject, controller.signal)
      .then((dashboard) => setData(dashboard))
      .catch((e) => {
        if (controller.signal.aborted) return;
        setData(null);
        setError(e instanceof Error ? e.message : 'The dashboard could not be loaded');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [companyId, selectedProject]);


  const chartOptions = {
    pieWithLegend: {
      responsive: true,
      plugins: { legend: { position: 'bottom' as const, labels: { usePointStyle: true, padding: 20 } }, tooltip: { callbacks: { label: (ctx: any) => {
        const label = ctx.label || '';
        const value = ctx.parsed || 0;
        const percentage = ctx.raw?.percentage ?? ctx.raw?.count ?? null;
        return typeof percentage === 'number' ? `${label}: ${formatCurrency(value)} (${percentage.toFixed(1)}%)` : `${label}: ${formatCurrency(value)}`;
      } } } }
    } satisfies ChartOptions<'pie'>,
    doughnut: {
      responsive: true,
      cutout: '60%',
      plugins: { legend: { position: 'bottom' as const, labels: { usePointStyle: true, padding: 20 } }, tooltip: { callbacks: { label: (ctx: any) => {
        const label = ctx.label || '';
        const count = ctx.parsed || 0;
        const percentage = ctx.raw?.percentage ?? 0;
        return `${label}: ${count} bills (${percentage.toFixed(1)}%)`;
      } } } }
    } satisfies ChartOptions<'doughnut'>,
    bar: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => formatCurrency(ctx.parsed.y) } } },
      scales: { x: { grid: { display: false }, ticks: { maxRotation: 0, minRotation: 0 } }, y: { grid: { color: '#D1D5DB' /* matches --gray-300; kept literal, see pieColors comment above re: canvas + CSS vars */ }, ticks: { callback: (value: any) => {
        const n = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(n) ? formatCurrency(n) : `${value}`;
      } } } }
    } satisfies ChartOptions<'bar'>,
  };

  const chartSeries = useMemo(() => {
    if (!data) {
      return { 
        outstandingByAmount: { labels: [], datasets: [] }, 
        openBillsByProject: { labels: [], datasets: [] }, 
        cashSpent: { labels: [], datasets: [] }, 
        committedButUnpaid: { labels: [], datasets: [] }, 
        apAging: { labels: [], datasets: [] } 
      };
    }
    
    // Add null checks for all data arrays
    const outstandingByAmount = data.outstandingByAmount || [];
    const openBillsByProject = data.openBillsByProject || [];
    const cashSpent = data.cashSpent || [];
    const apAging = data.apAging || [];
    const committedButUnpaidByProject = data.committedButUnpaidByProject || [];
    
    const outstandingLabels = outstandingByAmount.map(i => i.project);
    const openCountLabels = openBillsByProject.map(i => i.project);
    const cashSpentLabels = cashSpent.map(e => e.month);
    const cashSpentValues = cashSpent.map(e => e.amount);
    const agingLabels = apAging.map(e => e.range);
    const agingValues = apAging.map(e => e.amount);
    const committedButUnpaidValues = committedButUnpaidByProject.map(i => i.amount);
    const committedButUnpaidLabels = committedButUnpaidByProject.map(i => i.project);
    
    return {
      outstandingByAmount: { 
        labels: outstandingLabels, 
        datasets: [{ 
          label: 'Outstanding Balance', 
          data: outstandingByAmount.map(i => ({ value: Number(i.amount.toFixed(2)), percentage: i.percentage })), 
          parsing: { key: 'value' }, 
          backgroundColor: outstandingLabels.map((_, idx) => pieColors[idx % pieColors.length]) 
        }] 
      } as ChartData<'pie', { value: number; percentage: number }[], string>,
      committedButUnpaid: { 
        labels: committedButUnpaidLabels, 
        datasets: [{ 
          label: 'Committed but Unpaid', 
          data: committedButUnpaidValues, 
          backgroundColor: '#6366F1', /* matches --indigo-500; kept literal, see pieColors comment re: canvas + CSS vars */
          borderRadius: 6
        }] 
      } as ChartData<'bar'>,
      openBillsByProject: { 
        labels: openCountLabels, 
        datasets: [{ 
          label: 'Open Bill Count', 
          data: openBillsByProject.map(i => ({ value: i.count, percentage: i.percentage })), 
          parsing: { key: 'value' }, 
          backgroundColor: openCountLabels.map((_, idx) => pieColors[idx % pieColors.length]) 
        }] 
      } as ChartData<'doughnut', { value: number; percentage: number }[], string>,
      cashSpent: { 
        labels: cashSpentLabels, 
        datasets: [{ 
          label: 'Cash Spent', 
          data: cashSpentValues, 
          backgroundColor: '#3B82F6', /* matches --blue-500; kept literal, see pieColors comment re: canvas + CSS vars */
          borderRadius: 6
        }] 
      } as ChartData<'bar'>,
      apAging: { 
        labels: agingLabels, 
        datasets: [{ 
          label: 'Outstanding Balance', 
          data: agingValues, 
          backgroundColor: '#F59E0B', /* matches --amber-500; kept literal, see pieColors comment re: canvas + CSS vars */
          borderRadius: 6
        }] 
      } as ChartData<'bar'>,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center text-gray-500">
          <p className="font-medium text-gray-700">{error ? 'The dashboard could not be loaded' : 'No data available'}</p>
          {error && <p className="text-sm mt-1">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Outstanding</h3>
          <p className="font-bold text-gray-900 leading-tight break-words" style={responsiveNumberStyle}>{formatCurrency(data.totalOutstanding)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Paid This Month</h3>
          <p className="font-bold text-green-600 leading-tight break-words" style={responsiveNumberStyle}>{formatCurrency(data.paidThisMonth)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Overdue Bills</h3>
          <p className="font-bold text-red-600 leading-tight break-words" style={responsiveNumberStyle}>{data.overdueBills}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Draft Bills</h3>
          <p className="font-bold text-yellow-600 leading-tight break-words" style={responsiveNumberStyle}>{data.draftBills}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Outstanding by Project</h3>
          <div className="h-[25rem] md:h-[31.25rem]">
            {data.outstandingByAmount.length > 0 ? <Pie data={chartSeries.outstandingByAmount} options={chartOptions.pieWithLegend} /> : <p className="text-center text-gray-500 py-8">No open bills{selectedProject !== 'all' ? ' for this project' : ''}</p>}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Open Bills by Project</h3>
          <div className="h-[25rem] md:h-[31.25rem]">
            {data.openBillsByProject.length > 0 ? <Doughnut data={chartSeries.openBillsByProject} options={chartOptions.doughnut} /> : <p className="text-center text-gray-500 py-8">No open bills{selectedProject !== 'all' ? ' for this project' : ''}</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Cash Spent (Last 6 Months)</h3>
          <div className="h-80">
            <Bar data={chartSeries.cashSpent} options={chartOptions.bar} />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Committed but Unpaid</h3>
          <div className="h-80">
            <Bar data={chartSeries.committedButUnpaid} options={chartOptions.bar} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">AP Aging Buckets</h3>
          <div className="h-80">
            <Bar data={chartSeries.apAging} options={chartOptions.bar} />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Top Vendors by Amount Owed</h3>
          <div className="h-80 overflow-y-auto">
            <div className="space-y-3">
              {(data.topVendorsByAmountOwed || []).map((vendor, index) => (
                <div key={vendor.vendor} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{vendor.vendor}</p>
                      <p className="text-sm text-gray-500">{vendor.count} bill{vendor.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(vendor.amount)}</p>
                  </div>
                </div>
              ))}
              {(!data.topVendorsByAmountOwed || data.topVendorsByAmountOwed.length === 0) && (
                <div className="text-center text-gray-500 py-8">
                  No vendor data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Upcoming Payments</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Bill</th>
                <th className="text-left py-2">Project</th>
                <th className="text-right py-2">Amount</th>
                <th className="text-right py-2">Days Until Due</th>
              </tr>
            </thead>
            <tbody>
              {(data.upcomingPayments || []).length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-gray-500">Nothing is due</td></tr>
              )}
              {(data.upcomingPayments || []).slice(0, 10).map((p, idx) => (
                <tr key={idx} className="border-b">
                  <td className="py-2">{p.bill}</td>
                  <td className="py-2">{p.project}</td>
                  <td className="text-right py-2">{formatCurrency(p.amount)}</td>
                  <td className="text-right py-2">{p.daysUntilDue} {p.daysUntilDue === 1 ? 'day' : 'days'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Bill Status Breakdown</h3>
        <div className="flex items-center space-x-4">
          {(data.billStatus || []).map((status, index) => (
            <div key={status.status} className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: `hsl(${index * 120}, 70%, 50%)` }} />
              <span className="text-sm">{status.status}: {status.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BillsDashboard;

